import { useEffect, useMemo, useState } from "react";
import type { ReactNode } from "react";
import {
  createGroup,
  listDiscoverableGroups,
  listMyGroups,
  listRecommendedGroups,
  requestJoinGroup,
  ensureMyPartnerGroups,
} from "../api/groups";

type GroupItem = {
  group_id: string;
  kind?: string;
  label?: string;
  description?: string;
  visibility?: string;
  join_policy?: string;
  is_discoverable?: number | boolean;
  is_recommendable?: number | boolean;
  group_type?: string | null;
  posting_policy?: string | null;
  lifecycle_policy?: string | null;
  role_name?: string;
  member_count?: number;
  shared_member_count?: number;
};

type RoomBehavior = {
  value: string;
  label: string;
  help: string;
  discoverable: boolean;
  recommendable: boolean;
};

const ROOM_BEHAVIORS: RoomBehavior[] = [
  {
    value: "interest_group",
    label: "Open room",
    help: "Good for most topics. People can discover it and request to join.",
    discoverable: true,
    recommendable: true,
  },
  {
    value: "study_group",
    label: "Planning room",
    help: "Good for homework, shared tasks, planning, and recurring coordination.",
    discoverable: true,
    recommendable: true,
  },
  {
    value: "event_group",
    label: "Event room",
    help: "Good for temporary dates, trips, meetups, movie nights, or activities.",
    discoverable: false,
    recommendable: false,
  },
  {
    value: "private_group",
    label: "Private room",
    help: "Invite-only. Not discoverable by default.",
    discoverable: false,
    recommendable: false,
  },
];

function groupLabel(g: GroupItem) {
  return g.label || g.group_id || "Room";
}

function groupTypeLabel(g: GroupItem) {
  const type = String(g.group_type || g.kind || "private_group");
  if (type === "interest_group") return "Open";
  if (type === "study_group") return "Planning";
  if (type === "event_group") return "Event";
  if (type === "private_group") return "Private";
  return "Room";
}

function postingPolicyLabel(g: GroupItem) {
  const policy = String(g.posting_policy || "members");
  if (policy === "moderators_only") return "Moderators only";
  if (policy === "admins_only") return "Admins only";
  if (policy === "announcements") return "Announcements";
  return "Members can post";
}

function lifecycleLabel(g: GroupItem) {
  return String(g.lifecycle_policy || "") === "ephemeral" ? "auto-archives when empty" : "persistent";
}

function saveLastOpenedGroup(groupId: string) {
  try {
    window.localStorage.setItem("intimsync.last_opened_group_id", groupId);
  } catch {}
}


function groupChatUrl(groupId: string) {
  return `/messages?originGroupId=${encodeURIComponent(groupId)}`;
}

function readLastOpenedGroup() {
  try {
    return window.localStorage.getItem("intimsync.last_opened_group_id") || "";
  } catch {
    return "";
  }
}

function readLastChatUrl() {
  try {
    return window.localStorage.getItem("intimsync.last_chat_url") || "";
  } catch {
    return "";
  }
}

function readLastChatLabel() {
  try {
    return window.localStorage.getItem("intimsync.last_chat_label") || "";
  } catch {
    return "";
  }
}

function readLastChatMode() {
  try {
    return window.localStorage.getItem("intimsync.last_chat_mode") || "";
  } catch {
    return "";
  }
}

function GroupCard({
  group,
  action,
  meta,
}: {
  group: GroupItem;
  action?: ReactNode;
  meta?: ReactNode;
}) {
  return (
    <div className="card" style={{ marginBottom: "0.75rem" }}>
      <div className="section">
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "minmax(0, 1fr) auto",
            gap: 12,
            alignItems: "start",
          }}
        >
          <div>
            <div style={{ fontWeight: 800 }}>{groupLabel(group)}</div>

            <p className="text-muted small" style={{ marginTop: 6 }}>
              {group.description || "No description"}
            </p>

            <div className="text-muted small" style={{ marginTop: 8 }}>
              {groupTypeLabel(group)} · {postingPolicyLabel(group)}
              {group.lifecycle_policy ? ` · ${lifecycleLabel(group)}` : ""}
              {group.member_count != null ? ` · ${group.member_count} members` : ""}
              {group.shared_member_count != null ? ` · ${group.shared_member_count} shared` : ""}
            </div>

            {meta}
          </div>

          <div className="chip-row" style={{ justifyContent: "flex-end" }}>
            <a
              className="btn-primary"
              href={groupChatUrl(group.group_id)}
              onClick={() => {
                saveLastOpenedGroup(group.group_id);
                try {
                  window.localStorage.setItem("intimsync.last_chat_url", groupChatUrl(group.group_id));
                  window.localStorage.setItem("intimsync.last_chat_label", groupLabel(group));
                  window.localStorage.setItem("intimsync.last_chat_mode", "group");
                } catch {}
              }}
              title="Open chat"
              aria-label="Open chat"
            >
              Chat
            </a>

            <a
              className="btn-outline"
              href={`/groups/${encodeURIComponent(group.group_id)}`}
              onClick={() => saveLastOpenedGroup(group.group_id)}
              title="Room settings"
              aria-label="Room settings"
            >
              ⚙
            </a>

            {action}
          </div>
        </div>
      </div>
    </div>
  );
}

export default function GroupsDiscoverablePage() {
  const [newGroupOpen, setNewGroupOpen] = useState(false);
  const [newGroupLabel, setNewGroupLabel] = useState("");
  const [newGroupDescription, setNewGroupDescription] = useState("");
  const [newGroupType, setNewGroupType] = useState("interest_group");
  const [newGroupTags, setNewGroupTags] = useState("");
  const [newGroupPostingPolicy, setNewGroupPostingPolicy] = useState("members");
  const [newGroupDiscoverable, setNewGroupDiscoverable] = useState(true);
  const [newGroupRecommendable, setNewGroupRecommendable] = useState(true);
  const [creatingGroup, setCreatingGroup] = useState(false);

  const [myGroups, setMyGroups] = useState<GroupItem[]>([]);
  const [discoverable, setDiscoverable] = useState<GroupItem[]>([]);
  const [recommended, setRecommended] = useState<GroupItem[]>([]);
  const [tab, setTab] = useState<"mine" | "join" | "recommended">("mine");
  const [q, setQ] = useState("");
  const [statusMsg, setStatusMsg] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const lastOpenedGroupId = useMemo(() => readLastOpenedGroup(), []);
  const lastChatUrl = useMemo(() => readLastChatUrl(), []);
  const lastChatLabel = useMemo(() => readLastChatLabel(), []);
  const lastChatMode = useMemo(() => readLastChatMode(), []);

  const selectedBehavior = ROOM_BEHAVIORS.find((x) => x.value === newGroupType) || ROOM_BEHAVIORS[0];

  async function load() {
    setError(null);

    try {
      await ensureMyPartnerGroups().catch(() => null);

      const [mineRes, discoverableRes, recommendedRes] = await Promise.all([
        listMyGroups(),
        listDiscoverableGroups(q.trim()),
        listRecommendedGroups(),
      ]);

      setMyGroups(Array.isArray(mineRes?.items) ? mineRes.items : []);
      setDiscoverable(Array.isArray(discoverableRes?.items) ? discoverableRes.items : []);
      setRecommended(Array.isArray(recommendedRes?.items) ? recommendedRes.items : []);
    } catch (e: any) {
      setError(e?.message || "Could not load rooms.");
    }
  }

  useEffect(() => {
    void load();
  }, []);

  function applyBehavior(value: string) {
    const behavior = ROOM_BEHAVIORS.find((x) => x.value === value) || ROOM_BEHAVIORS[0];

    setNewGroupType(behavior.value);
    setNewGroupDiscoverable(behavior.discoverable);
    setNewGroupRecommendable(behavior.recommendable);
  }

  async function handleRequestJoin(groupId: string) {
    setStatusMsg(null);
    setError(null);

    try {
      const res = await requestJoinGroup(groupId, "");
      setStatusMsg(
        res?.joined
          ? "Joined room."
          : `Join request submitted${res?.status ? `: ${res.status}` : "."}`
      );
      await load();
    } catch (e: any) {
      setError(e?.message || "Could not request to join.");
    }
  }

  async function handleCreateGroup() {
    const label = newGroupLabel.trim();
    if (!label) return;

    setCreatingGroup(true);
    setError(null);
    setStatusMsg(null);

    try {
      const res = await createGroup({
        label,
        description: newGroupDescription.trim() || undefined,
        groupType: newGroupType,
        topicTags: newGroupTags,
        postingPolicy: newGroupPostingPolicy,
        visibility: newGroupType === "private_group" ? "private" : "public",
        joinPolicy: newGroupType === "private_group" ? "invite_only" : "public_request",
        isDiscoverable: newGroupDiscoverable,
        isRecommendable: newGroupRecommendable,
      });

      const groupId = res?.item?.group_id || res?.group_id;
      if (groupId) {
        window.location.href = `/groups/${encodeURIComponent(groupId)}`;
        return;
      }

      setNewGroupLabel("");
      setNewGroupDescription("");
      setNewGroupTags("");
      setNewGroupOpen(false);
      setStatusMsg("Room created.");
      await load();
    } catch (e: any) {
      setError(e?.message || "Could not create room.");
    } finally {
      setCreatingGroup(false);
    }
  }

  const myIds = new Set(myGroups.map((g) => g.group_id));
  const joinable = discoverable.filter((g) => !myIds.has(g.group_id));
  const lastOpened = myGroups.find((g) => g.group_id === lastOpenedGroupId) || myGroups[0] || null;

  return (
    <div>
      <div className="page-header">
        <div>
          <div className="page-title">Groups</div>
          <div className="page-subtitle">
            Create or join rooms about anything.
          </div>
        </div>
      </div>

      {!!statusMsg && (
        <div className="text-xs text-emerald-300 bg-emerald-950/30 border border-emerald-800 rounded-lg px-3 py-2 mb-3">
          {statusMsg}
        </div>
      )}

      {!!error && (
        <div className="text-xs text-rose-300 bg-rose-950/30 border border-rose-800 rounded-lg px-3 py-2 mb-3">
          {error}
        </div>
      )}

      <div className="card" style={{ marginBottom: "1rem" }}>
        <div className="section">
          <div style={{ display: "flex", justifyContent: "space-between", gap: 12, alignItems: "center" }}>
            <div>
              <div className="section-title">Create a room</div>
              <p className="text-muted small" style={{ marginTop: 4 }}>
                What is this room about?
              </p>
            </div>

            <button
              className={newGroupOpen ? "btn-primary" : "btn-outline"}
              onClick={() => setNewGroupOpen((v) => !v)}
            >
              {newGroupOpen ? "Close" : "New room"}
            </button>
          </div>

          {newGroupOpen && (
            <div style={{ marginTop: 12 }}>
              <input
                className="form-input"
                placeholder="Room name"
                value={newGroupLabel}
                onChange={(e) => setNewGroupLabel(e.target.value)}
              />

              <textarea
                className="form-textarea"
                rows={3}
                placeholder="What should people use this room for?"
                value={newGroupDescription}
                onChange={(e) => setNewGroupDescription(e.target.value)}
                style={{ marginTop: 10 }}
              />

              <input
                className="form-input"
                placeholder="Tags, comma separated"
                value={newGroupTags}
                onChange={(e) => setNewGroupTags(e.target.value)}
                style={{ marginTop: 10 }}
              />

              <div className="chip-row" style={{ marginTop: 10 }}>
                <select
                  className="form-input"
                  value={newGroupType}
                  onChange={(e) => applyBehavior(e.target.value)}
                  style={{ maxWidth: 260 }}
                >
                  {ROOM_BEHAVIORS.map((behavior) => (
                    <option key={behavior.value} value={behavior.value}>
                      {behavior.label}
                    </option>
                  ))}
                </select>

                <select
                  className="form-input"
                  value={newGroupPostingPolicy}
                  onChange={(e) => setNewGroupPostingPolicy(e.target.value)}
                  style={{ maxWidth: 260 }}
                >
                  <option value="members">All members can post</option>
                  <option value="moderators_only">Moderators only</option>
                  <option value="admins_only">Admins only</option>
                  <option value="announcements">Announcements</option>
                </select>
              </div>

              <p className="text-muted small" style={{ marginTop: 8 }}>
                {selectedBehavior.help}
              </p>

              <div className="chip-row" style={{ marginTop: 10 }}>
                <button
                  className={newGroupDiscoverable ? "btn-primary" : "btn-outline"}
                  onClick={() => setNewGroupDiscoverable((v) => !v)}
                >
                  Discoverable: {newGroupDiscoverable ? "on" : "off"}
                </button>

                <button
                  className={newGroupRecommendable ? "btn-primary" : "btn-outline"}
                  onClick={() => setNewGroupRecommendable((v) => !v)}
                >
                  Recommendations: {newGroupRecommendable ? "on" : "off"}
                </button>

                <button
                  className="btn-primary"
                  onClick={() => void handleCreateGroup()}
                  disabled={creatingGroup || !newGroupLabel.trim()}
                >
                  {creatingGroup ? "Creating…" : "Create"}
                </button>
              </div>
            </div>
          )}
        </div>
      </div>

      {(lastChatUrl || lastOpened) && (
        <div className="card" style={{ marginBottom: "1rem" }}>
          <div className="section">
            <div className="section-title">Continue</div>

            <div style={{ fontWeight: 800 }}>
              {lastChatUrl ? lastChatLabel || "Last chat" : groupLabel(lastOpened as GroupItem)}
            </div>

            <p className="text-muted small" style={{ marginTop: 6 }}>
              {lastChatUrl
                ? lastChatMode === "private"
                  ? "Last private chat"
                  : "Last group chat"
                : lastOpened?.group_id === lastOpenedGroupId
                ? "Last opened room"
                : "Default room"}
            </p>

            <div className="chip-row" style={{ marginTop: 10 }}>
              {lastChatUrl ? (
                <a className="btn-primary" href={lastChatUrl}>
                  Open chat
                </a>
              ) : (
                <a
                  className="btn-primary"
                  href={`/groups/${encodeURIComponent((lastOpened as GroupItem).group_id)}`}
                  onClick={() => saveLastOpenedGroup((lastOpened as GroupItem).group_id)}
                >
                  Open room
                </a>
              )}

              {lastOpened && (
                <a
                  className="btn-outline"
                  href={`/groups/${encodeURIComponent(lastOpened.group_id)}`}
                  onClick={() => saveLastOpenedGroup(lastOpened.group_id)}
                >
                  Room details
                </a>
              )}
            </div>
          </div>
        </div>
      )}

      <div className="card">
        <div className="section">
          <div className="chip-row">
            <button className={tab === "mine" ? "btn-primary" : "btn-outline"} onClick={() => setTab("mine")}>
              My rooms ({myGroups.length})
            </button>
            <button className={tab === "join" ? "btn-primary" : "btn-outline"} onClick={() => setTab("join")}>
              Discover ({joinable.length})
            </button>
            <button
              className={tab === "recommended" ? "btn-primary" : "btn-outline"}
              onClick={() => setTab("recommended")}
            >
              Recommended ({recommended.length})
            </button>
          </div>
        </div>

        {tab === "mine" && (
          <div className="section">
            <div className="section-title">My rooms</div>

            {!myGroups.length && (
              <p className="text-muted small">You are not a member of any rooms yet.</p>
            )}

            {myGroups.map((g) => (
              <GroupCard
                key={g.group_id}
                group={g}
                meta={
                  <p className="text-muted small" style={{ marginTop: 6 }}>
                    role: {g.role_name || "member"}
                  </p>
                }
              />
            ))}
          </div>
        )}

        {tab === "join" && (
          <div className="section">
            <div className="section-title">Discover rooms</div>

            <div className="chip-row" style={{ marginBottom: 12 }}>
              <input
                className="form-input"
                placeholder="Search rooms"
                value={q}
                onChange={(e) => setQ(e.target.value)}
                style={{ minWidth: 220 }}
              />
              <button className="btn-outline" onClick={() => void load()}>
                Search
              </button>
            </div>

            {!joinable.length && (
              <p className="text-muted small">No discoverable rooms available.</p>
            )}

            {joinable.map((g) => (
              <GroupCard
                key={g.group_id}
                group={g}
                action={
                  g.join_policy === "public_request" ? (
                    <button className="btn-primary" onClick={() => void handleRequestJoin(g.group_id)}>
                      Request join
                    </button>
                  ) : null
                }
              />
            ))}
          </div>
        )}

        {tab === "recommended" && (
          <div className="section">
            <div className="section-title">Recommended rooms</div>
            <p className="text-muted small">
              Recommendations are based on rooms that people from your existing rooms also participate in.
            </p>

            {!recommended.length && (
              <p className="text-muted small">No recommendations yet.</p>
            )}

            {recommended.map((g) => (
              <GroupCard
                key={g.group_id}
                group={g}
                action={
                  g.join_policy === "public_request" ? (
                    <button className="btn-primary" onClick={() => void handleRequestJoin(g.group_id)}>
                      Request join
                    </button>
                  ) : null
                }
              />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
